import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './FlexPrint.module.css';
import { formatCurrency, normalizeTipoFluxo } from '../../../../utils/formatters';
import { TIPO_FLUXO, TIPO_CORRECAO, ALL_PLANOS, getTipoParcela } from '../../../../utils/constantes';

// Header Component
const Header = ({ isMRV }) => (
  <div className={styles.printHeaderRow}>
    <img
      src={isMRV ? "img/logo_mrv.webp" : "img/logo_sensia.png"}
      alt={isMRV ? "MRV Logo" : "Sensia Logo"}
    />
  </div>
);

Header.propTypes = {
  isMRV: PropTypes.bool
};

// Property Information Component
const PropertyInfo = ({ propertyData }) => (
  <div className={styles.headerColumns}>
    <div className={styles.headerCol1}>
      <div className={styles.infoLine}>
        <strong>Empreendimento:</strong> <span>{propertyData?.selectedProperty || 'N/A'}</span>
      </div>
      <div className={styles.infoLine}>
        <strong>Unidade:</strong> <span>{propertyData?.selectedUnit || 'N/A'}</span>
      </div>
    </div>
    <div className={styles.headerCol2}>
      <div className={styles.infoLine}>
        <strong>Valor Imóvel:</strong> <span>{formatCurrency(propertyData?.propertyValue) || 'N/A'}</span>
      </div>
      <div className={styles.infoLine}>
        <strong>Tabela de Vendas:</strong> <span>{propertyData?.priceTableVersion || 'N/A'}</span>
      </div>
    </div>
  </div>
);

PropertyInfo.propTypes = {
  propertyData: PropTypes.shape({
    selectedProperty: PropTypes.string,
    selectedUnit: PropTypes.string,
    propertyValue: PropTypes.number,
    priceTableVersion: PropTypes.string
  })
};

// Info Section Component (reusable)
const InfoSection = ({ title, children }) => (
  <div className={styles.infoSection}>
    <div className={styles.sectionTitle}>{title}</div>
    <div className={styles.sectionContent}>
      {children}
    </div>
  </div>
);

InfoSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};

// Info Line Component (reusable)
const InfoLine = ({ label, value, highlight = false }) => (
  <div className={`${styles.infoLineItem} ${highlight ? styles.highlight : ''}`}>
    <span className={styles.infoLabel}>{label}:</span>
    <span className={styles.infoValue}>{value}</span>
  </div>
);

InfoLine.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node]),
  highlight: PropTypes.bool
};

// 1. Identificação da Simulação
const IdentificationSection = ({ simulationResult, formData, propertyData }) => {
  const currentDate = new Date().toLocaleDateString('pt-BR');
  
  return (
    <InfoSection title="Identificação da Simulação">
      <InfoLine label="Data da Simulação" value={currentDate} />
      <InfoLine label="ID da Simulação" value={simulationResult?.idProposta || 'N/A'} />
    </InfoSection>
  );
};

IdentificationSection.propTypes = {
  simulationResult: PropTypes.object,
  formData: PropTypes.object,
  propertyData: PropTypes.object
};

// 2. Localização / Empreendimento
const LocationSection = ({ propertyData }) => (
  <InfoSection title="Localização / Empreendimento">
    <InfoLine label="Cidade" value={propertyData?.cidade || 'N/A'} />
    <InfoLine label="Empreendimento" value={propertyData?.selectedProperty || 'N/A'} />
    <InfoLine label="Bloco" value={propertyData?.bloco || 'N/A'} />
    <InfoLine label="Unidade" value={propertyData?.selectedUnit || 'N/A'} />
  </InfoSection>
);

LocationSection.propTypes = {
  propertyData: PropTypes.object
};

// 3. Produto / Configuração Comercial
const ProductConfigSection = ({ simulationResult, propertyData }) => (
  <InfoSection title="Produto / Configuração Comercial">
    <InfoLine label="Linha de Produto" value={propertyData?.linhaProduto || 'N/A'} />
    <InfoLine label="AMP" value={`${(simulationResult?.amp || 0).toFixed(2)}%`} />
    <InfoLine label="Prazo de Obra" value={simulationResult?.prazoObra || 0} />
    <InfoLine label="Plano de Venda" value={propertyData?.planoVenda || 'N/A'} />
    <InfoLine label="É SAD?" value={simulationResult?.sad ? 'Sim' : 'Não'} />
    <InfoLine label="É Pagadoria?" value={simulationResult?.pagadoria ? 'Sim' : 'Não'} />
    <InfoLine label="Tipo de Canal" value={simulationResult?.tipoCanal || 'N/A'} />
    <InfoLine label="Cliente tem renda exterior?" value={propertyData?.isRendaExterior ? 'Sim' : 'Não'} />
    <InfoLine label="Equipe" value={simulationResult?.equipe || 'N/A'} />
    {simulationResult?.pagadoria && (
      <InfoLine label="Valor Comissão (via pagadoria)" value={formatCurrency(simulationResult?.valorComissao)} />
    )}
    <InfoLine label="Indicador" value={`${((simulationResult?.margem || 0) ).toFixed(2)}%`} />
  </InfoSection>
);

ProductConfigSection.propTypes = {
  simulationResult: PropTypes.object,
  propertyData: PropTypes.object
};

// 4. Regras de Correção / Indexador
const CorrectionRulesSection = ({ simulationResult, propertyData }) => {
  const getTipoFluxoLabel = (tipo) => {
    const tipoNormalizado = normalizeTipoFluxo(tipo);
    return TIPO_FLUXO[tipoNormalizado] || tipo || 'N/A';
  };

  const getTipoCorrecaoLabel = (tipo) => {
    return TIPO_CORRECAO[tipo] || tipo || 'N/A';
  };

  return (
    <InfoSection title="Regras de Correção / Indexador">
      <InfoLine label="Tipo de Correção" value={getTipoCorrecaoLabel(simulationResult?.tipoCorrecao)} />
      <InfoLine label="Fonte da Projeção" value={'Boletim Focus - Banco Central (conforme contrato)'} />
      <InfoLine label="Tipo de Fluxo" value={getTipoFluxoLabel(simulationResult?.tipoFluxo)} />
      <InfoLine label="Parcelas" value={`${simulationResult?.numeroParcela || 0}`} />
    </InfoSection>
  );
};

CorrectionRulesSection.propTypes = {
  simulationResult: PropTypes.object,
  propertyData: PropTypes.object
};

// 5. Valores e Ajustes Comerciais
const CommercialValuesSection = ({ simulationResult, formData, propertyData }) => {
  // O planoVenda em propertyData já contém o nome completo do plano
  const isFinanciamentoBancario = propertyData?.planoVenda === ALL_PLANOS.FinanciamentoBancario;
  
  // Verificar se o plano exige exibição de renda (Prazo de Obra, Pós Obra, Financiamento Bancário)
  const shouldShowRenda = propertyData?.planoVenda === ALL_PLANOS.PrazoObra || 
                          propertyData?.planoVenda === ALL_PLANOS.PosObra || 
                          propertyData?.planoVenda === ALL_PLANOS.FinanciamentoBancario;
  
  return (
    <InfoSection title="Valores e Ajustes Comerciais">
      {shouldShowRenda && (
        <InfoLine label="Renda" value={formatCurrency(formData?.renda)} />
      )}
      <InfoLine label="Valor Tabela" value={formatCurrency(simulationResult?.valorTabela)} />
      <InfoLine label="Aumento" value={formatCurrency(simulationResult?.aumento)} />
      <InfoLine label="BP Pagadoria" value={formatCurrency(simulationResult?.bomPagadorPagadoria)} />
      <InfoLine label="Desconto de Alçada" value={formatCurrency(simulationResult?.desconto)} />
      <InfoLine label="Valor Final" value={formatCurrency(simulationResult?.valorFinal)} />
      {isFinanciamentoBancario && (
        <InfoLine label="Valor Financiado" value={formatCurrency(simulationResult?.valorFinanciado)} />
      )}
    </InfoSection>
  );
};

CommercialValuesSection.propTypes = {
  simulationResult: PropTypes.object,
  formData: PropTypes.object,
  propertyData: PropTypes.object
};

// 6. Utilização de FGTS
const FGTSSection = ({ simulationResult, propertyData }) => {
  const isFinanciamentoBancario = propertyData?.planoVenda === ALL_PLANOS.FinanciamentoBancario;
  const valorFinalComFgts = simulationResult?.valorFinalComFgts || 0;
  const valorFinal = simulationResult?.valorFinal || 0;
  const valorFinanciado = simulationResult?.valorFinanciado || 0;
  const percentualFinanciado = valorFinal > 0 
    ? ((valorFinanciado / valorFinal) * 100).toFixed(1)
    : 0;

  return (
    <InfoSection title="Utilização de FGTS">
      <InfoLine label="FGTS" value={formatCurrency(simulationResult?.fgts)} />
      <InfoLine label="Valor Final (com FGTS)" value={formatCurrency(valorFinalComFgts)} highlight />
      {isFinanciamentoBancario && (
        <InfoLine 
          label={`Valor Financiado (${percentualFinanciado}%)`} 
          value={formatCurrency(valorFinanciado)} 
        />
      )}
    </InfoSection>
  );
};

FGTSSection.propTypes = {
  simulationResult: PropTypes.object,
  propertyData: PropTypes.object
};

FGTSSection.propTypes = {
  simulationResult: PropTypes.object
};

// 7. Estrutura de Pagamento
const PaymentStructureSection = ({ simulationResult }) => {
  if (!simulationResult) return null;

  const parcelas = simulationResult.parcelas || [];
  
  // Obter valores das entradas (sinais) - tipo 3
  const sinais = parcelas.filter(p => p.tipo === 3);
  
  // Ordenar por data de vencimento para pegar na ordem correta
  const sinaisOrdenados = sinais.sort((a, b) => 
    new Date(a.dataVencimento) - new Date(b.dataVencimento)
  );
  
  const ato = sinaisOrdenados[0]; // Primeira entrada
  const e2 = sinaisOrdenados[1];  // Segunda entrada
  const e3 = sinaisOrdenados[2];  // Terceira entrada
  
  // Calcular intermediárias
  const totalIntermediarias = parcelas
    .filter(p => p.tipo === 1)
    .reduce((acc, p) => acc + (p.valorNominal || 0), 0);

  return (
    <InfoSection title="Estrutura de Pagamento">
      <InfoLine label="Ato (1ª Parcela)" value={formatCurrency(ato?.valorNominal)} />
      <InfoLine label="PSA Padrão" value={formatCurrency(simulationResult?.psaPadrao)} />
      <InfoLine label="E2 (2ª Parcela)" value={formatCurrency(e2?.valorNominal)} />
      <InfoLine label="PSB Padrão" value={formatCurrency(simulationResult?.psbPadrao)} />
      <InfoLine label="E3 (3ª Parcela)" value={formatCurrency(e3?.valorNominal)} />
      <InfoLine label="Total Intermediárias" value={formatCurrency(totalIntermediarias)} />
    </InfoSection>
  );
};

PaymentStructureSection.propTypes = {
  simulationResult: PropTypes.object
};

// 8. Grupos de Parcelas
const GruposSection = ({ simulationResult }) => {
  const informativoGrupos = simulationResult?.informativoGrupos || [];
  
  if (!informativoGrupos || informativoGrupos.length === 0) {
    return null;
  }

  return (
    <InfoSection title="Grupos de Parcelas">
      <div className={styles.tableContainer}>
        <table className={styles.tablePrint}>
          <thead>
            <tr>
              <th>Grupo</th>
              <th>Valor Nominal da Parcela (R$)</th>
            </tr>
          </thead>
          <tbody>
            {informativoGrupos.map((grupo, idx) => (
              <tr key={idx}>
                <td>{grupo.nomeGrupo}</td>
                <td>{formatCurrency(grupo.valorNominalParcela)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </InfoSection>
  );
};

GruposSection.propTypes = {
  simulationResult: PropTypes.object
};
const ParcelasDetailTable = ({ parcelas, simulationResult }) => {
  if (!parcelas || parcelas.length === 0) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <InfoSection title="Detalhamento das Parcelas">
      <div className={styles.tableContainer}>
        <table className={styles.tablePrint}>
          <thead>
            <tr>
              <th>#</th>
              <th>Vencimento</th>
              <th>Tipo</th>
              <th>Valor Nominal (R$)</th>
              <th>Fator Variação</th>
              <th>Projeção (R$)</th>
            </tr>
          </thead>
          <tbody>
            {parcelas.map((parcela, idx) => (
              <tr key={parcela.id && parcela.id !== '00000000-0000-0000-0000-000000000000' ? parcela.id : `${parcela.dataVencimento}-${parcela.tipo}-${idx}`}>
                <td>{idx + 1}</td>
                <td>{formatDate(parcela.dataVencimento)}</td>
                <td>{getTipoParcela(parcela, idx, parcelas)}</td>
                <td>{formatCurrency(parcela.valorNominal)}</td>
                <td>{simulationResult?.projecao?.[idx]?.toFixed(4)}</td>
                <td>{formatCurrency(parcela.valorReal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </InfoSection>
  );
};

ParcelasDetailTable.propTypes = {
  parcelas: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      dataVencimento: PropTypes.string,
      tipo: PropTypes.number,
      valorNominal: PropTypes.number,
      valorReal: PropTypes.number
    })
  ),
  simulationResult: PropTypes.object
};

// Page Footer Component
const PageFooter = ({ simulationResult, currentDate }) => (
  <div className={styles.footerPrint}>
    <div>
      <p>Esta simulação apresenta valores e condições meramente estimativos, sujeitos a alteração a qualquer momento. As condições válidas serão as constantes do contrato de compra e venda e, se houver, do financiamento firmado na aquisição.</p>
      <span style={{ marginLeft: 16 }}>Impresso em {currentDate}</span>
      <br />
      <span>ID: {simulationResult?.idProposta || 'N/A'}</span>
    </div>
  </div>
);

PageFooter.propTypes = {
  simulationResult: PropTypes.shape({
    idProposta: PropTypes.string
  }),
  currentDate: PropTypes.string.isRequired
};

// Main FlexPrint Component
const FlexPrint = ({ simulationResult, propertyData, formData, isMRV }) => {
  const currentDate = new Date().toLocaleDateString('pt-BR');
  const printRef = useRef();
  const parcelas = simulationResult?.parcelas || [];

  return (
    <div id="flex-area-impressao" className={styles.printArea} ref={printRef}>
      <Header isMRV={isMRV} />
      
      {/* All Information Sections */}
      <IdentificationSection 
        simulationResult={simulationResult} 
        formData={formData}
        propertyData={propertyData} 
      />
      
      <LocationSection propertyData={propertyData} />
      
      <ProductConfigSection 
        simulationResult={simulationResult}
        propertyData={propertyData}
      />
      
      <CorrectionRulesSection 
        simulationResult={simulationResult}
        propertyData={propertyData}
      />
      
      <CommercialValuesSection 
        simulationResult={simulationResult} 
        formData={formData} 
        propertyData={propertyData} 
      />
      
      <FGTSSection simulationResult={simulationResult} propertyData={propertyData} />
      
      <PaymentStructureSection simulationResult={simulationResult} />
      
      <GruposSection simulationResult={simulationResult} />
      
      <ParcelasDetailTable parcelas={parcelas} simulationResult={simulationResult} />
      
      <PageFooter simulationResult={simulationResult} currentDate={currentDate} />
    </div>
  );
};

FlexPrint.propTypes = {
  simulationResult: PropTypes.shape({
    idProposta: PropTypes.string,
    linhaProduto: PropTypes.string,
    tipoCanal: PropTypes.string,
    equipe: PropTypes.string,
    prazoObra: PropTypes.number,
    sad: PropTypes.bool,
    pagadoria: PropTypes.bool,
    tipoCorrecao: PropTypes.string,
    projecao: PropTypes.string,
    tipoFluxo: PropTypes.string,
    valorTabela: PropTypes.number,
    aumento: PropTypes.number,
    desconto: PropTypes.number,
    amp: PropTypes.number,
    bomPagadorPagadoria: PropTypes.number,
    valorFinal: PropTypes.number,
    fgts: PropTypes.number,
    valorFinalComFgts: PropTypes.number,
    valorComissao: PropTypes.number,
    margem: PropTypes.number,
    numeroParcela: PropTypes.number,
    psaPadrao: PropTypes.number,
    psbPadrao: PropTypes.number,
    valorPSBTotal: PropTypes.number,
    parcelas: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        dataVencimento: PropTypes.string,
        tipo: PropTypes.number,
        valorNominal: PropTypes.number,
        valorReal: PropTypes.number,
        tipoSinal: PropTypes.number
      })
    ),
    informativoGrupos: PropTypes.arrayOf(
      PropTypes.shape({
        nomeGrupo: PropTypes.string,
        valorNominalParcela: PropTypes.number
      })
    )
  }),
  propertyData: PropTypes.shape({
    selectedProperty: PropTypes.string,
    selectedUnit: PropTypes.string,
    propertyValue: PropTypes.number,
    priceTableVersion: PropTypes.string,
    cidade: PropTypes.string,
    bloco: PropTypes.string,
    planoVenda: PropTypes.string,
    isRendaExterior: PropTypes.bool
  }),
  formData: PropTypes.object,
  isMRV: PropTypes.bool
};

FlexPrint.defaultProps = {
  isMRV: true
};

export default FlexPrint;
